import type {
  AtendenteDTO,
  ConversaDetalheDTO,
  ConversaResumoDTO,
  EventosCliente,
  EventosServidor,
  MensagemDTO,
} from '@viniko/shared';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { notificar } from './avisos';
import { chaves } from './consultas';

export type EstadoConexao = 'conectando' | 'conectado' | 'desconectado';

function upsertMensagem(qc: QueryClient, m: MensagemDTO) {
  qc.setQueryData<ConversaDetalheDTO>(chaves.conversa(m.conversaId), (c) => {
    if (!c) return c;
    const existe = c.mensagens.some((x) => x.id === m.id);
    const mensagens = existe
      ? c.mensagens.map((x) => (x.id === m.id ? m : x))
      : [...c.mensagens, m];
    return { ...c, mensagens };
  });
}

export function useTempoReal(eu: AtendenteDTO | undefined): EstadoConexao {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [estado, setEstado] = useState<EstadoConexao>('conectando');
  const avisadas = useRef(new Map<string, string>());

  useEffect(() => {
    if (!eu) return;
    const socket: Socket<EventosServidor, EventosCliente> = io({
      path: '/socket.io',
      withCredentials: true,
    });

    socket.on('connect', () => {
      setEstado('conectado');
      qc.invalidateQueries({ queryKey: ['conversas'] });
      qc.invalidateQueries({ queryKey: ['conversa'] });
    });
    socket.on('disconnect', () => setEstado('desconectado'));
    socket.on('connect_error', () => setEstado('desconectado'));

    socket.on('conversa:atualizada', (c: ConversaResumoDTO) => {
      qc.invalidateQueries({ queryKey: ['conversas'] });
      qc.invalidateQueries({ queryKey: ['treino'] });
      qc.invalidateQueries({ queryKey: ['pedidos'] });
      qc.setQueryData<ConversaDetalheDTO>(chaves.conversa(c.id), (d) => (d ? { ...d, ...c } : d));
      qc.invalidateQueries({ queryKey: chaves.conversa(c.id) });

      const ultima = c.ultimaMensagem;
      if (!ultima || c.silenciada || avisadas.current.get(c.id) === ultima.em) return;
      const abrir = () => navegar(`/conversas/${c.id}`);
      const minha =
        c.status === 'atendimento' && c.atendente?.id === eu.id && ultima.autor === 'cliente';
      const naFila = c.status === 'fila' && (ultima.autor === 'cliente' || ultima.autor === 'ia');
      if (minha) notificar(c.contato.nome, ultima.texto, abrir);
      else if (naFila)
        notificar(
          'Nova conversa na fila',
          `${c.contato.nome} · ${c.motivoTransferencia ?? ''}`,
          abrir,
        );
      else return;
      avisadas.current.set(c.id, ultima.em);
    });

    socket.on('conversa:removida', ({ id }) => {
      qc.removeQueries({ queryKey: chaves.conversa(id) });
      qc.invalidateQueries({ queryKey: ['conversas'] });
      if (window.location.pathname.includes(id)) navegar('/conversas', { replace: true });
    });

    socket.on('mensagem:nova', (m) => {
      upsertMensagem(qc, m);
      if (m.autor === 'sistema') qc.invalidateQueries({ queryKey: ['tiny'] });
    });

    socket.on('mensagem:status', ({ id, conversaId, statusEntrega }) => {
      qc.setQueryData<ConversaDetalheDTO>(chaves.conversa(conversaId), (c) =>
        c
          ? { ...c, mensagens: c.mensagens.map((m) => (m.id === id ? { ...m, statusEntrega } : m)) }
          : c,
      );
    });

    socket.on('atendente:status', () => qc.invalidateQueries({ queryKey: chaves.atendentes }));

    socket.on('notificacao:mencao', ({ conversaId, de, texto }) => {
      notificar(`${de} mencionou você`, texto, () => navegar(`/conversas/${conversaId}`));
    });

    return () => {
      socket.disconnect();
    };
  }, [eu?.id, qc, navegar]); // eslint-disable-line react-hooks/exhaustive-deps

  return estado;
}
